// user-client/src/routes/index.jsx
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';

import ProtectedRoute from './ProtectedRoute';
import { NightHouseSsoAuto, NightHouseSsoEntry } from './NightHouseSso';
import { clearSignupAgent } from '../shared/utils/signupAgent';
import { clearPendingReferralCode } from '../shared/utils/pendingReferral';
import { normalizeTableCode } from '../shared/utils/tableCode';
import { RuntimeStateProvider } from '../state/RuntimeStateProvider';
import { installAuthWindowLease } from '../shared/utils/authPersistence';
import DesktopLoginScreen from '../desktop/screens/auth/LoginScreen';
import MobileLoginScreen from '../mobile/screens/auth/LoginScreen';

// Keep the first login paint synchronous (same UX as before); split all secondary
// auth/table routes so they are downloaded only when entered.
const DesktopSignupPolicyScreen = lazy(() => import('../desktop/screens/auth/SignupPolicyScreen'));
const MobileSignupPolicyScreen = lazy(() => import('../mobile/screens/auth/SignupPolicyScreen'));
const DesktopSignupFormScreen = lazy(() => import('../desktop/screens/auth/SignupFormScreen'));
const MobileSignupFormScreen = lazy(() => import('../mobile/screens/auth/SignupFormScreen'));
const DesktopAccountRecoveryScreen = lazy(() => import('../desktop/screens/auth/AccountRecoveryScreen'));
const MobileAccountRecoveryScreen = lazy(() => import('../mobile/screens/auth/AccountRecoveryScreen'));
const AgentJoinEntry = lazy(() => import('../shared/screens/auth/AgentJoinEntry'));
const WelcomeEntry = lazy(() => import('../shared/screens/auth/WelcomeEntry'));
const DesktopTableScreen = lazy(() => import('../desktop/screens/DesktopTableScreen'));
const MobileTableScreen = lazy(() => import('../mobile/screens/MobileTableScreen'));

function GeneralSignupEntry() {
  useEffect(() => {
    clearSignupAgent();
    clearPendingReferralCode();
  }, []);
  return <Navigate to="/signup/policy" replace />;
}


function LoungeVipX2GuideEntry({ Root, lobbyVariant }) {
  return (
    <Root
      key="lounge:vip-x2-guide"
      lobbyVariant={lobbyVariant}
      initialViewKey="lounge"
      initialLoungePostCode=""
      initialLoungeVipGuideOpen
    />
  );
}

function LoungeEntry({ Root, lobbyVariant }) {
  const { postCode } = useParams();
  const normalizedPostCode = String(postCode || '').trim();
  if (!normalizedPostCode) return <Navigate to="/lobby" replace />;

  return (
    <Root
      key={`lounge:${normalizedPostCode}`}
      lobbyVariant={lobbyVariant}
      initialViewKey="lounge"
      initialLoungePostCode={normalizedPostCode}
    />
  );
}

function TableEntry({ isMobileRuntime }) {
  const { tableCode } = useParams();
  const [sp] = useSearchParams();

  const tableId = normalizeTableCode(
    tableCode || sp.get('tableCode') || sp.get('table_code') || sp.get('table_id') || ''
  );

  const isPopup =
    typeof window !== 'undefined' &&
    String(window.location?.pathname || '').startsWith('/popup');

  useEffect(() => {
    if (!tableId) return undefined;
    return installAuthWindowLease({ routeKind: isPopup ? 'popup' : 'table' });
  }, [isPopup, tableId]);

  if (!tableId) return <Navigate to="/lobby" replace />;

  const tableScreen = isMobileRuntime
    ? <MobileTableScreen tableId={tableId} isPopup={isPopup} />
    : <DesktopTableScreen tableId={tableId} isPopup={isPopup} />;

  return <RuntimeStateProvider>{tableScreen}</RuntimeStateProvider>;
}

export default function AppRouter({ Root, mode }) {
  const isMobileRuntime = mode === 'mobile';
  const lobbyVariant = isMobileRuntime ? 'mobile' : 'desktop';

  const loginElement = isMobileRuntime ? <MobileLoginScreen /> : <DesktopLoginScreen />;
  const signupPolicyElement = isMobileRuntime ? <MobileSignupPolicyScreen /> : <DesktopSignupPolicyScreen />;
  const signupFormElement = isMobileRuntime ? <MobileSignupFormScreen /> : <DesktopSignupFormScreen />;
  const accountRecoveryElement = isMobileRuntime ? <MobileAccountRecoveryScreen /> : <DesktopAccountRecoveryScreen />;

  return (
    <BrowserRouter>
      <NightHouseSsoAuto />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/login" element={loginElement} />
          <Route path="/nh-sso" element={<NightHouseSsoEntry />} />

          <Route path="/signup" element={<GeneralSignupEntry />} />
          <Route path="/welcome/:agentCode" element={<WelcomeEntry />} />
          <Route path="/join/:agentCode" element={<AgentJoinEntry />} />
          <Route path="/signup/policy" element={signupPolicyElement} />
          <Route path="/signup/form" element={signupFormElement} />
          <Route path="/account-recovery" element={accountRecoveryElement} />
          <Route path="/account-recovery/reset" element={accountRecoveryElement} />
          <Route path="/account-recovery/verify-email" element={accountRecoveryElement} />

          <Route
            path="/lobby"
            element={<Root key="lobby" lobbyVariant={lobbyVariant} />}
          />

          <Route
            path="/lobby/lounge/vip-x2-guide"
            element={<LoungeVipX2GuideEntry Root={Root} lobbyVariant={lobbyVariant} />}
          />

          <Route
            path="/lobby/lounge/:postCode"
            element={<LoungeEntry Root={Root} lobbyVariant={lobbyVariant} />}
          />

          <Route
            path="/"
            element={<Root key="root" lobbyVariant={lobbyVariant} />}
          />

          <Route
            path="/table/:tableCode"
            element={
              <ProtectedRoute allowGuestSpectator>
                <TableEntry isMobileRuntime={isMobileRuntime} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/popup"
            element={
              <ProtectedRoute allowGuestSpectator>
                <TableEntry isMobileRuntime={isMobileRuntime} />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
